
export const reportTemplates = {
  marketAnalysis: {
    id: "market-analysis",
    name: "Market Analysis",
    description: "Comprehensive market research and analysis",
    prompt: `Conduct a comprehensive market analysis covering:

1. Market Size & Growth
   - Current market size and historical growth trends
   - Projected growth rates and future outlook
   - Key market drivers and restraints

2. Competitive Landscape
   - Major players and market share
   - Competitive positioning
   - Recent market developments

3. Customer Segments
   - Target customer demographics
   - Customer needs and pain points
   - Buying behavior patterns

4. Market Trends
   - Emerging trends and innovations
   - Technology adoption
   - Regulatory impacts

5. Opportunities & Threats
   - Market opportunities
   - Potential risks and challenges
   - Strategic recommendations

Please provide detailed analysis with data-driven insights.`,
  },
  competitorResearch: {
    id: "competitor-research",
    name: "Competitor Research",
    description: "In-depth competitor analysis",
    prompt: `Perform detailed competitor research including:

1. Company Overview
   - Business model and value proposition
   - Product/service offerings
   - Target market and positioning

2. Financial Performance
   - Revenue and profitability trends
   - Growth metrics
   - Funding and valuation

3. Strengths & Weaknesses
   - Core competencies
   - Competitive advantages
   - Areas of vulnerability

4. Market Strategy
   - Go-to-market approach
   - Marketing and sales tactics
   - Partnerships and alliances

5. SWOT Analysis
   - Comprehensive assessment
   - Strategic implications
   - Competitive response recommendations`,
  },
  industryTrends: {
    id: "industry-trends",
    name: "Industry Trends",
    description: "Emerging trends and future outlook",
    prompt: `Analyze current and emerging industry trends:

1. Technology Trends
   - Emerging technologies
   - Innovation hotspots
   - Adoption patterns

2. Market Dynamics
   - Shifting customer preferences
   - New business models
   - Disruption patterns

3. Regulatory Environment
   - Policy changes
   - Compliance requirements
   - Industry standards

4. Economic Factors
   - Macroeconomic influences
   - Investment trends
   - Market conditions

5. Future Outlook
   - 3-5 year projections
   - Opportunity areas
   - Strategic recommendations`,
  },
  investmentThesis: {
    id: "investment-thesis",
    name: "Investment Thesis",
    description: "Investment opportunity analysis",
    prompt: `Develop a comprehensive investment thesis covering:

1. Investment Opportunity
   - Company/sector overview
   - Value proposition
   - Market opportunity

2. Financial Analysis
   - Historical performance
   - Financial projections
   - Valuation metrics

3. Competitive Position
   - Market positioning
   - Competitive advantages
   - Barriers to entry

4. Risk Assessment
   - Key risks and mitigants
   - Scenario analysis
   - Risk-return profile

5. Investment Recommendation
   - Investment rationale
   - Target allocation
   - Exit strategy considerations`,
  },
  productStrategy: {
    id: "product-strategy",
    name: "Product Strategy & Market Entry",
    description: "Comprehensive product opportunity analysis with go-to-market strategy",
    prompt: `Develop a strategic product opportunity analysis covering:

1. Product Concept & Market Validation
   - Product idea and core value proposition
   - Target market and specific pain points addressed
   - Market size estimate and growth potential

2. Timing & Trend Analysis
   - Why now? Current market drivers and trends
   - Technology readiness and adoption curves
   - Regulatory and economic factors

3. Competitive Landscape
   - Direct and indirect competitors
   - Competitive positioning and differentiation strategy
   - Potential competitive responses and market reactions

4. Go-to-Market Strategy
   - Early adopter personas and customer segments
   - Geographic market prioritization and expansion sequence
   - Distribution channels and partnership opportunities
   - Pricing strategy and validation approach

5. Business Model & Scaling
   - Revenue streams and monetization strategy
   - Unit economics and profitability timeline
   - Expansion potential and scalability factors
   - Risk mitigation and success metrics

Provide specific, actionable recommendations with data-driven insights.`,
  },
  dueDiligence: {
    id: "due-diligence",
    name: "Due Diligence",
    description: "Comprehensive due diligence report",
    prompt: `Conduct thorough due diligence analysis:

1. Business Overview
   - Company history and operations
   - Management team
   - Corporate structure

2. Financial Review
   - Financial statements analysis
   - Revenue quality
   - Cash flow assessment

3. Operational Analysis
   - Business processes
   - Technology infrastructure
   - Supply chain

4. Legal & Compliance
   - Legal structure
   - Regulatory compliance
   - Litigation history

5. Risks & Opportunities
   - Key risks identified
   - Value creation opportunities
   - Deal considerations`,
  },
};

export type TemplateId = keyof typeof reportTemplates;
